// parseEvents.js with priority field support

// 优化解析CSV文件的方法，处理特殊字符和数组格式
export const parseEvents = (csvContent, callback, maxTime = 1800, timeframe = "11AM") => {
  if (!csvContent) {
    // console.error("No CSV content provided to parseEvents");
    callback([]);
    return;
  }

  // console.log("Parse Events Started with timeframe:", timeframe);
  
  try {
    // 分割行
    const lines = csvContent.trim().split('\n');
    
    // 获取标题行
    const headers = lines[0].trim().split(',').map(h => h.trim());
    // console.log("CSV Headers:", headers);
    
    const events = [];
    
    // 逐行处理
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // 跳过空行
      
      // 基本解析 - 但不处理 JSON 数组
      let values = [];
      let inQuotes = false;
      let currentValue = '';
      let arrayDepth = 0;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        
        if (char === '"' && (j === 0 || line[j-1] !== '\\')) {
          inQuotes = !inQuotes;
          currentValue += char;
        } else if (char === '[') {
          arrayDepth++;
          currentValue += char;
        } else if (char === ']') {
          arrayDepth--;
          currentValue += char;
        } else if (char === ',' && !inQuotes && arrayDepth === 0) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      
      // 添加最后一个值
      if (currentValue) {
        values.push(currentValue.trim());
      }
      
      // console.log(`行 ${i} 解析得到 ${values.length} 个值:`, values);
      
      // 创建事件对象
      const event = {};
      for (let j = 0; j < headers.length; j++) {
        if (j < values.length) {
          const header = headers[j];
          const value = values[j];
          
          // 尝试解析 JSON 数组 (bboxes 和 similarVideos)
          if (header === 'bboxes' && value && (value.startsWith('[') && value.endsWith(']'))) {
            try {
              // 尝试解析 bboxes JSON 数组
              let parsedBboxes = JSON.parse(value);
              
              // 确保 bboxes 是数组
              if (!Array.isArray(parsedBboxes)) {
                parsedBboxes = [];
              }
              
              // 处理每个 bbox 对象, 确保字段正确
              parsedBboxes = parsedBboxes.map((bbox, idx) => {
                // 标准化 id 和 category 字段
                if (!bbox.id && bbox.ID) bbox.id = bbox.ID;
                
                // 从 label 中提取 category
                if (!bbox.category && bbox.label) {
                  // 尝试从 label 中提取类别 (例如 "person 0.92" -> "person")
                  const match = /^([a-zA-Z]+)/.exec(bbox.label);
                  if (match && match[1]) {
                    bbox.category = match[1].toLowerCase();
                  }
                }
                
                // 如果没有 category，设置默认值
                if (!bbox.category) {
                  bbox.category = idx % 2 === 0 ? 'person' : 'vehicle';
                }
                
                // 确保有特征对象
                if (!bbox.features) {
                  bbox.features = {};
                }
                
                // 处理图像路径
                if (bbox.image_path) {
                  // 如果是相对路径，确保格式正确
                  if (!bbox.image_path.startsWith('http') && !bbox.image_path.startsWith('/')) {
                    bbox.image_path = `/${timeframe === "11AM" ? "day" : "night"}/images/${bbox.image_path}`;
                  }
                }
                
                return bbox;
              });
              
              event[header] = parsedBboxes;
              // console.log(`成功解析 ${header}:`, event[header]);
            } catch (e) {
              console.error(`解析 ${header} 时出错:`, e);
              console.log("原始字符串:", value);
              event[header] = [];
            }
          } else if (header === 'similarVideos' && value && (value.startsWith('[') && value.endsWith(']'))) {
            try {
              event[header] = JSON.parse(value);
            } catch (e) {
              console.error(`解析 ${header} 时出错:`, e);
              event[header] = [];
            }
          } else {
            // 常规数据处理
            event[header] = value;
          }
        }
      }
      
      // 提取必要的字段并添加颜色
      const type = parseInt(event.Type, 10) || 0;
      // 解析优先级字段
      const priority = parseInt(event.priority, 10) || 0;
      
      // 计算开始和结束时间/位置
      const startFrame = parseInt(event.frame_start, 10);
      const endFrame = parseInt(event.frame_end, 10);
      
      // 转换到秒
      const fps = 30; // 假设帧率为30fps
      const startTime = startFrame / fps;
      const endTime = endFrame / fps;
      
      // 计算位置比例
      const startPosition = Math.min(1, Math.max(0, startTime / maxTime));
      const endPosition = Math.min(1, Math.max(0, endTime / maxTime));
      
      const eventObj = {
        type,
        priority, // 添加优先级字段
        event: event.Event,
        startTime,
        endTime,
        startPosition,
        endPosition,
        color: getEventColor(type, priority), // 更新以考虑优先级
        hoverColor: getHoverColor(type, priority), // 更新以考虑优先级
        probability: event.probability || event.probability_level || 0,
        summary: event.summary || "",
        justification: event.justification || "", 
        imagePath: event.image_path || "",
        bboxes: event.bboxes || [],
        similarVideos: event.similarVideos || []
      };
      
      events.push(eventObj);
    }
    
    // 在进行排序
    events.sort((a, b) => a.startTime - b.startTime);
    
    // console.log(`事件已解析 (${events.length} 个事件)`, events);
    callback(events);
  } catch (error) {
    console.error("解析事件时出错:", error);
    callback([]);
  }
};

// 更新颜色函数以考虑优先级
export const getEventColor = (type, priority = 0) => {
  const typeNum = Number(type);
  const priorityNum = Number(priority);
  
  // 增加颜色的透明度或者强度基于优先级
  let alpha = 0.2; // 默认透明度
  if (priorityNum >= 3) {
    alpha = 0.4; // 高优先级 - 更明显
  } else if (priorityNum >= 2) {
    alpha = 0.3; // 中优先级
  }
  
  if (typeNum >= 1 && typeNum <= 3) {
    // 橙黄色主题 - 使用不同的橙黄色调
    return `rgba(255, 191, 0, ${alpha})`;
  } else if (typeNum >= 4 && typeNum <= 6) {
    return `rgba(220, 20, 60, ${alpha})`;
  } else {
    // 默认颜色
    return `rgba(156, 163, 175, ${alpha})`;
  }
};

// 更新悬停颜色函数以考虑优先级
export const getHoverColor = (type, priority = 0) => {
  const typeNum = Number(type);
  const priorityNum = Number(priority);
  
  // 增加颜色的透明度或者强度基于优先级
  let alpha = 0.5; // 默认悬停透明度
  if (priorityNum >= 3) {
    alpha = 0.7; // 高优先级 - 更明显
  } else if (priorityNum >= 2) {
    alpha = 0.6; // 中优先级
  }
  
  if (typeNum >= 1 && typeNum <= 3) {
    // 橙黄色主题 - 使用不同的橙黄色调
    return `rgba(255, 191, 0, ${alpha})`;
  } else if (typeNum >= 4 && typeNum <= 6) {
    return `rgba(220, 20, 60, ${alpha})`;
  } else {
    // 默认颜色
    return `rgba(156, 163, 175, ${alpha})`;
  }
};
 
export const getEventBgColor = (type, priority = 0) => {
  const typeNum = Number(type);
  const priorityNum = Number(priority);
  
  // 增加颜色的透明度或者强度基于优先级
  let alpha = 0.15; // 默认背景透明度
  if (priorityNum >= 3) {
    alpha = 0.3; // 高优先级 - 更明显
  } else if (priorityNum >= 2) {
    alpha = 0.2; // 中优先级
  }
  
  if (typeNum >= 1 && typeNum <= 3) {
    // 橙黄色主题 - 使用不同的橙黄色调
    return `rgba(255, 191, 0, ${alpha})`;
  } else if (typeNum >= 4 && typeNum <= 6) {
    return `rgba(220, 20, 60, ${alpha})`;
  } else {
    // 默认颜色
    return `rgba(156, 163, 175, ${alpha})`;
  }
};

export const getEventBorderColor = (type, priority = 0) => {
  const typeNum = Number(type);
  const priorityNum = Number(priority);
  
  // 增加颜色的透明度或者强度基于优先级
  let alpha = 0.5; // 默认边框透明度
  if (priorityNum >= 3) {
    alpha = 0.8; // 高优先级 - 更明显
  } else if (priorityNum >= 2) {
    alpha = 0.65; // 中优先级
  }
  
  if (typeNum >= 1 && typeNum <= 3) {
    // 橙黄色主题 - 使用不同的橙黄色调
    return `rgba(255, 191, 0, ${alpha})`;
  } else if (typeNum >= 4 && typeNum <= 6) {
    return `rgba(220, 20, 60, ${alpha})`;
  } else {
    // 默认颜色
    return `rgba(156, 163, 175, ${alpha})`;
  }
};

export const styles = {
  container: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
  },
  eventMarker: {
    position: "absolute",
    height: "95%",
    pointerEvents: "auto",
    top: 0,
    cursor: "pointer",
  },
  eventInner: {
    height: "100%",
    borderRadius: "2px",
    transition: "opacity 0.2s, background-color 0.2s",
  },
  tooltip: {
    position: "absolute",
    zIndex: 50,
    backgroundColor: "white",
    padding: "8px",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
    fontSize: "12px",
    whiteSpace: "nowrap",
    pointerEvents: "none",
  },
};